import { createSlice, 
         PayloadAction 
       }                                              from "@reduxjs/toolkit";
import { RootState }                                  from "store/Store";
import { APIData }                                    from "types/cms/frontendTypes";
import { GetRecoForUtility, 
         QA_WORKFLOW, 
         RECO_EDIT_STATUS 
       }                                              from "types/apis/recoInterfaces";
import { ApiErrorInfo }                               from "types";
import { RecoUtils }                                  from "features/recommendations/utils/RecoUtils";
import { cloneDeep }                                  from "lodash";
import { CMS_DEFAULT_LOCALE }                         from "./cmsPilotSlice";

export interface RecoReducerState {
  initDone        : boolean
  error          ?: ApiErrorInfo
  draftRecos      : APIData<'api::recommendation.recommendation'>[]
  publishedRecos  : APIData<'api::recommendation.recommendation'>[]
  modifiedRecos   : APIData<'api::recommendation.recommendation'>[]
  count           : {
    draft : number
    published : number
    modified : number
    disabled : number
  }
  unsavedRows: Record<string, Partial<APIData<'api::recommendation.recommendation'>>>;
  hasUnsavedRows: boolean;
  recoRanking : {
    [key : string] : number
  }
  categoryToRecoMap : {
    [key : string] : string[]
  }
  tableState : {
    filters: {
      items: { field: string; operator: string; value: any }[];
    };
    selectedLocale: string;
    showOnlyUnsaved: boolean;
  }
  reviewWorkflow ?: QA_WORKFLOW 
}

const defaultState : RecoReducerState = {
  initDone : false,
  error : undefined,
  draftRecos : [],
  publishedRecos : [],
  modifiedRecos : [],
  count : {
    draft : 0,
    published : 0,
    modified : 0,
    disabled : 0
  },
  unsavedRows: {},
  recoRanking : {},
  categoryToRecoMap : {},
  hasUnsavedRows: false,
  tableState: {
    filters: {
      items: [],
    },
    selectedLocale: CMS_DEFAULT_LOCALE,
    showOnlyUnsaved: false,
  },
}

export const recommendationsSlice = createSlice({
  name : 'recommendations',
  initialState : defaultState,
  reducers : {
    setRecos : (state : RecoReducerState, action : PayloadAction<{reco : GetRecoForUtility.retval, 
                                                                  recoScore : APIData<'api::utility-reco-score.utility-reco-score'>}>) => {
      state.initDone = true
      state.draftRecos = action.payload.reco.data.draft
      state.publishedRecos = action.payload.reco.data.published
      state.modifiedRecos = action.payload.reco.data.modified
      state.count = action.payload.reco.meta.count
      state.error = undefined

      updateRecoRanking(state, action.payload.recoScore)
    },

    setRecoError : (state : RecoReducerState, action : PayloadAction<ApiErrorInfo>) => {
      state.error = action.payload
      state.initDone = true
    },

    setUnsavedRowAction: (state: RecoReducerState, action: PayloadAction<{
      id: string;
      diff: Partial<APIData<'api::recommendation.recommendation'>>;
      recoScore : APIData<'api::utility-reco-score.utility-reco-score'>
    }>) => {

      state.unsavedRows[action.payload.id] = action.payload.diff;
      state.hasUnsavedRows = true;
      updateRecoRanking(state, action.payload.recoScore)
    },

    discardUnsavedRowAction: (state: RecoReducerState, action: PayloadAction<{
      id: string;
      recoScore : APIData<'api::utility-reco-score.utility-reco-score'>
    }>) => {

      delete state.unsavedRows[action.payload.id];
      if (Object.keys(state.unsavedRows).length === 0) {
        state.hasUnsavedRows = false;
      }
      updateRecoRanking(state, action.payload.recoScore)
    },

    discardAllUnsavedRowsAction: (state: RecoReducerState) => {
      state.unsavedRows = {};
      state.hasUnsavedRows = false;
    },

    createRecoAction: (state: RecoReducerState, action: PayloadAction<{
      reco : APIData<'api::recommendation.recommendation'>,
      recoScore : APIData<'api::utility-reco-score.utility-reco-score'>
    }>) => {

      state.draftRecos.push(action.payload.reco)
      state.count.draft++
      updateRecoRanking(state, action.payload.recoScore)
    },

    updateModifiedRecosAction: (state: RecoReducerState, action : PayloadAction<{
      recos : APIData<'api::recommendation.recommendation'>[],
      recoScore : APIData<'api::utility-reco-score.utility-reco-score'>
    }>) => {
      
      const updatedRecos = action.payload.recos

      updatedRecos.forEach((rec) => {
        
        const docId = rec.documentId
       
        // Remove from unsaved rows
        const unsavedReco = Object.values(state.unsavedRows).find((r) => r.documentId === docId)
        if (unsavedReco) delete state.unsavedRows[unsavedReco.id!]
        
        // Save new recos from backend
        if ((rec as any).editStatus === RECO_EDIT_STATUS.DRAFT) {
          const index = state.draftRecos.findIndex(r => r.documentId === docId)
          if (index !== -1) {
            state.draftRecos[index] = rec
          } else {
            state.draftRecos.push(rec)
            state.count.draft++
            state.draftRecos.sort((a, b) => (a.recommendationId > b.recommendationId ? 1 : -1))
          }
        } else if ((rec as any).editStatus === RECO_EDIT_STATUS.MODIFIED) {
          const index = state.modifiedRecos.findIndex(r => r.documentId === docId)
          if (index !== -1) {
            state.modifiedRecos[index] = rec
          } else {
            state.modifiedRecos.push(rec)
            state.count.modified++
            state.modifiedRecos.sort((a, b) => (a.recommendationId > b.recommendationId ? 1 : -1))
          }
        } else {
          // Specific disable / enable case
          const index = state.publishedRecos.findIndex(r => r.documentId === docId)
          if (index >= 0) {
            state.publishedRecos[index] = rec
            recomputeCount(state)
          } 
        }
      })  
      
      if (Object.keys(state.unsavedRows).length === 0) {
        state.hasUnsavedRows = false;
      }

      updateRecoRanking(state, action.payload.recoScore)
    },

    discardAllDraftRecosAction: (state: RecoReducerState, action: PayloadAction<string[]>) => {

      const docIds = action.payload

      for (const docId of docIds) {
        const index = state.modifiedRecos.findIndex(r => r.documentId === docId)
        if (index !== -1) {
          state.modifiedRecos.splice(index, 1)
          state.count.modified--
        } else {
          const index = state.draftRecos.findIndex(r => r.documentId === docId)
          if (index !== -1) {
            state.draftRecos.splice(index, 1)
            state.count.draft--
          }
        }
      }
    },

    publishRecosAction: (state: RecoReducerState, action: PayloadAction<APIData<'api::recommendation.recommendation'>[]>) => {
      
      const publishedRecos = action.payload
      
      for (const reco of publishedRecos) {

        const index = state.modifiedRecos.findIndex(r => r.documentId === reco.documentId)
        if (index !== -1) {
          state.modifiedRecos.splice(index, 1)
          state.count.modified--
        } else {
          const index = state.draftRecos.findIndex(r => r.documentId === reco.documentId)
          if (index !== -1) {
            state.draftRecos.splice(index, 1)
            state.count.draft--
          }
        }

        const existingPublishedReco = state.publishedRecos.findIndex(r => r.documentId === reco.documentId)
        if (existingPublishedReco !== -1) {
          state.publishedRecos[existingPublishedReco] = reco
        } else {
          state.publishedRecos.push(reco)
        }

        state.count.published = state.publishedRecos.length
      }

      state.publishedRecos.sort((a, b) => (a.recommendationId > b.recommendationId ? 1 : -1))
    },

    unpublishRecosAction: (state: RecoReducerState, action: PayloadAction<APIData<'api::recommendation.recommendation'>[]>) => {
      const draftRecos = action.payload

      for (const draftReco of draftRecos) {
        const index = state.publishedRecos.findIndex(r => r.documentId === draftReco.documentId)
        if (index !== -1) {
          state.publishedRecos.splice(index, 1)
          state.count.published--
        }

        state.draftRecos.push(draftReco)
        state.count.draft++
      }

      state.draftRecos.sort((a, b) => (a.recommendationId > b.recommendationId ? 1 : -1))
    },

    resetRecoState : (state : RecoReducerState) => {
      state.initDone = false;
      state.error = undefined;
      state.draftRecos = [];
      state.publishedRecos = [];
      state.modifiedRecos = [];
      state.count = {
        draft: 0,
        published: 0,
        modified: 0,
        disabled: 0
      };
      state.unsavedRows = {};
      state.hasUnsavedRows = false;
      state.recoRanking = {};
      state.categoryToRecoMap = {};
      state.tableState = {
        filters: {
          items: [],
        },
        selectedLocale: '',
        showOnlyUnsaved: false,
      }
    },

    setFiltersAction: (state: RecoReducerState, action: PayloadAction<{ field: string; operator: string; value: any }[]>) => {
      state.tableState.filters.items = action.payload;
    },

    clearFiltersAction: (state: RecoReducerState) => {
      state.tableState.filters.items = [];
    },

    setSelectedLocaleAction: (state: RecoReducerState, action: PayloadAction<string>) => {
      state.tableState.selectedLocale = action.payload;
    },

    setShowOnlyUnsavedAction: (state: RecoReducerState, action: PayloadAction<boolean>) => {
      state.tableState.showOnlyUnsaved = action.payload;
    },

    setReviewWorkflowAction: (state: RecoReducerState, action: PayloadAction<QA_WORKFLOW>) => {
      state.reviewWorkflow = action.payload;
    }
  } as any
}) as any

const updateRecoRanking = (state : RecoReducerState, 
                           recoScore : APIData<'api::utility-reco-score.utility-reco-score'>) => {

  const categoryToRecoMap : Record<string, string[]> = {} 
  const allRecos = [...state.publishedRecos, ...state.draftRecos, ...state.modifiedRecos]
  
  for (const reco of allRecos) {
    if (!categoryToRecoMap[reco.category]) categoryToRecoMap[reco.category] = []
    if (!categoryToRecoMap[reco.category].includes(reco.recommendationId)) categoryToRecoMap[reco.category].push(reco.recommendationId)
  }

  state.categoryToRecoMap = categoryToRecoMap

  const recos = [...state.draftRecos, ...state.modifiedRecos]
  recos.push(...state.publishedRecos.filter(
      (reco) => !recos.some((r) => r.recommendationId === reco.recommendationId)))

  for (const category in categoryToRecoMap) {
    const ranks = RecoUtils.computeRecoRanksForCategory(category, cloneDeep(recos), 
      cloneDeep(state.unsavedRows), recoScore)
    state.recoRanking = {
      ...state.recoRanking,
      ...ranks
    }
  }
}

const recomputeCount = (state : RecoReducerState) => {

  const draftRecosCount = state.draftRecos.filter((reco) => !reco.disabled).length
  const modifiedRecosCount = state.modifiedRecos.filter((reco) => !reco.disabled).length
  const publishedRecosCount = state.publishedRecos.filter((reco) => !reco.disabled).length

  const disabledRecosCount = state.draftRecos.length - draftRecosCount + 
                              state.modifiedRecos.length - modifiedRecosCount + 
                              state.publishedRecos.length - publishedRecosCount

  state.count = {
    draft : draftRecosCount,
    modified : modifiedRecosCount,
    published : publishedRecosCount,
    disabled : disabledRecosCount
  }
}

export const {
  setRecos,
  setRecoError,
  setUnsavedRowAction,
  updateSavedRowsAction,
  discardUnsavedRowAction,
  discardAllUnsavedRowsAction,
  createRecoAction,
  updateModifiedRecosAction,
  publishRecosAction,
  unpublishRecosAction,
  discardAllDraftRecosAction,
  resetRecoState,
  setFiltersAction,
  clearFiltersAction,
  setSelectedLocaleAction,
  setShowOnlyUnsavedAction,
  setReviewWorkflowAction
} = recommendationsSlice.actions;

export const selectDraftRecos = (state : RootState) => (state.recommendations as RecoReducerState).draftRecos;
export const selectPublishedRecos = (state : RootState) => (state.recommendations as RecoReducerState).publishedRecos;
export const selectModifiedRecos = (state : RootState) => (state.recommendations as RecoReducerState).modifiedRecos;
export const selectCount = (state : RootState) => (state.recommendations as RecoReducerState).count;  
export const selectInitDone = (state : RootState) => (state.recommendations as RecoReducerState).initDone;
export const selectRecoError = (state : RootState) => (state.recommendations as RecoReducerState).error;
export const selectUnsavedRows = (state: RootState) => (state.recommendations as RecoReducerState).unsavedRows;
export const selectHasUnsavedRows = (state: RootState) => (state.recommendations as RecoReducerState).hasUnsavedRows;
export const selectTableState = (state: RootState) => (state.recommendations as RecoReducerState).tableState;
export const selectShowOnlyUnsaved = (state: RootState) => (state.recommendations as RecoReducerState).tableState.showOnlyUnsaved;
export const selectRecoRanking = (state : RootState) => (state.recommendations as RecoReducerState).recoRanking;
export const selectReviewWorkflow = (state : RootState) => (state.recommendations as RecoReducerState).reviewWorkflow;